/**
 * Namespace `modules`: os módulos de CRUD do projeto (Riscos, Mudanças, Custos,
 * Pendências, EAP e RACI).
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde a tela repete
 * a mesma palavra em módulos diferentes ("Duplicar", "Arraste para reordenar",
 * "Selecionar data"), o dicionário repete também: cada módulo é dono do seu
 * texto e um dia pode divergir sem arrastar os outros.
 *
 * REGRA DOS ARRAYS DE OPÇÃO: `PROBABILITY_OPTIONS`, `STATUS_OPTIONS`,
 * `TYPE_OPTIONS`, `PRIORITY_OPTIONS` e `CRITICIDADE_OPTIONS` viraram
 * `*_VALUES` nos componentes, com `value`, cor e ícone. O `value` é o que vai
 * para o banco (`risk.status = 'open'`, `pendencia.criticidade = 'alta'`) e o
 * que `filter`/`switch` comparam: fica no código. Só o rótulo veio para cá,
 * indexado por esse mesmo `value`. As letras R/A/C/I do RACI seguem a mesma
 * regra: são valores gravados, não rótulos.
 */
const modules = {
  /** Gestão de Riscos: tabela, formulário e estatísticas do cabeçalho. */
  risks: {
    title: 'Gestão de Riscos',
    loading: 'Carregando riscos...',
    newRisk: 'Novo Risco',
    filterAll: 'Todos',
    /** Sufixo colado no título ao duplicar a linha. */
    copySuffix: ' (cópia)',
    /** Substantivo que a barra de seleção e o confirm de exclusão usam. */
    item: { one: 'risco', other: 'riscos' },
    selected: { one: '{{count}} risco selecionado', other: '{{count}} riscos selecionados' },
    dragHint: 'Arraste para reordenar',
    confirmDelete: 'Tem certeza que deseja excluir este risco?',
    firstVisit: 'Registre seu primeiro risco. Probabilidade e impacto posicionam ele no mapa de calor e mostram onde está sua maior exposição.',

    /** Palavra que segue o número em cada contador do cabeçalho. */
    stats: {
      total: 'total',
      open: 'abertos',
      mitigating: 'mitigando',
      critical: 'críticos',
    },

    empty: {
      title: 'Nenhum risco cadastrado',
      description: 'Adicione riscos para monitorar ameaças ao projeto.',
      action: 'Adicionar Primeiro Risco',
    },

    columns: {
      id: 'ID',
      risk: 'Risco',
      probability: 'Probabilidade',
      impact: 'Impacto',
      status: 'Status',
      owner: 'Responsável',
      dueDate: 'Data Limite',
      actions: 'Ações',
    },

    rowActions: {
      duplicate: 'Duplicar',
      edit: 'Editar',
      delete: 'Excluir',
    },

    probability: {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      very_high: 'Muito Alta',
    },
    impact: {
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto',
      critical: 'Crítico',
    },
    status: {
      open: 'Aberto',
      mitigating: 'Mitigando',
      closed: 'Fechado',
      occurred: 'Ocorreu',
    },

    form: {
      editTitle: 'Editar Risco',
      newTitle: 'Novo Risco',
      title: 'Título *',
      titlePlaceholder: 'Descrição breve do risco',
      description: 'Descrição',
      descriptionPlaceholder: 'Detalhes do risco...',
      probability: 'Probabilidade',
      impact: 'Impacto',
      status: 'Status',
      select: 'Selecione',
      owner: 'Responsável',
      ownerSelect: 'Selecione...',
      ownerPlaceholder: 'Nome do responsável',
      dueDate: 'Data Limite',
      datePlaceholder: 'Selecionar data',
      mitigation: 'Plano de Mitigação',
      mitigationPlaceholder: 'Ações para reduzir a probabilidade ou impacto...',
      contingency: 'Plano de Contingência',
      contingencyPlaceholder: 'Ações caso o risco ocorra...',
    },
  },

  /** Plano de Mudanças: solicitações, workflow de aprovação e impactos. */
  changes: {
    title: 'Plano de Mudanças',
    loading: 'Carregando mudanças...',
    newChange: 'Nova Mudança',
    filterAll: 'Todos',
    copySuffix: ' (cópia)',
    item: { one: 'mudança', other: 'mudanças' },
    selected: { one: '{{count}} mudança selecionada', other: '{{count}} mudanças selecionadas' },
    dragHint: 'Arraste para reordenar',
    confirmDelete: 'Tem certeza que deseja excluir esta mudança?',
    /** Sufixo de dias no impacto de prazo da tabela ("12d"). */
    daysSuffix: 'd',
    firstVisit: 'Abra sua primeira solicitação de mudança. Todo ajuste de escopo, prazo ou custo fica rastreável da abertura até a aprovação.',

    stats: {
      total: 'total',
      pending: 'pendentes',
      approved: 'aprovadas',
      impact: 'impacto',
    },

    empty: {
      title: 'Nenhuma mudança cadastrada',
      description: 'Registre solicitações de mudança do projeto.',
      action: 'Registrar Primeira Mudança',
    },

    columns: {
      id: 'ID',
      change: 'Mudança',
      type: 'Tipo',
      priority: 'Prioridade',
      status: 'Status',
      impacts: 'Impactos',
      requester: 'Solicitante',
      actions: 'Ações',
    },

    rowActions: {
      approve: 'Aprovar',
      reject: 'Rejeitar',
      duplicate: 'Duplicar',
      edit: 'Editar',
      delete: 'Excluir',
    },

    type: {
      scope: 'Escopo',
      schedule: 'Cronograma',
      cost: 'Custo',
      resource: 'Recurso',
      other: 'Outro',
    },
    status: {
      pending: 'Pendente',
      approved: 'Aprovada',
      rejected: 'Rejeitada',
      implemented: 'Implementada',
    },
    priority: {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      urgent: 'Urgente',
    },

    form: {
      editTitle: 'Editar Mudança',
      newTitle: 'Nova Solicitação de Mudança',
      title: 'Título *',
      titlePlaceholder: 'Descrição breve da mudança',
      description: 'Descrição',
      descriptionPlaceholder: 'Detalhes da solicitação...',
      type: 'Tipo',
      priority: 'Prioridade',
      status: 'Status',
      select: 'Selecione',
      requester: 'Solicitante',
      requesterSelect: 'Selecione...',
      requesterPlaceholder: 'Nome do solicitante',
      /** (R$) fica: a cobrança do produto é em real, não muda com o idioma. */
      costImpact: 'Impacto Custo (R$)',
      amountPlaceholder: '0.00',
      scheduleImpact: 'Impacto Prazo (dias)',
      daysPlaceholder: '0',
      impactAnalysis: 'Análise de Impacto',
      impactAnalysisPlaceholder: 'Descreva os impactos da mudança no projeto...',
    },
  },

  /** Controle de Custos: cards de resumo, distribuição por tipo e tabela. */
  costs: {
    title: 'Controle de Custos',
    loading: 'Carregando custos...',
    newCost: 'Novo Custo',
    filterAll: 'Todos os tipos',
    copySuffix: ' (cópia)',
    item: { one: 'custo', other: 'custos' },
    selected: { one: '{{count}} custo selecionado', other: '{{count}} custos selecionados' },
    dragHint: 'Arraste para reordenar',
    confirmDelete: 'Tem certeza que deseja excluir este custo?',
    firstVisit: 'Lance seu primeiro custo. Com planejado e realizado no mesmo lugar, a curva de valor agregado revela o desvio cedo.',

    summary: {
      planned: 'Orçamento Planejado',
      /** `{{count}}` lançamentos cadastrados no projeto. */
      entriesOne: '{{count}} lançamento',
      entriesOther: '{{count}} lançamentos',
      actual: 'Valor Realizado',
      /** `{{pct}}` do orçamento planejado já realizado. */
      ofPlanned: '{{pct}}% do planejado',
      variance: 'Variação',
      overBudget: 'Acima do orçamento',
      underBudget: 'Abaixo do orçamento',
    },

    breakdown: {
      title: 'Distribuição por Tipo',
      /** `{{value}}` já vem formatado como moeda. */
      actual: 'Realizado: {{value}}',
    },

    empty: {
      title: 'Nenhum custo cadastrado',
      description: 'Registre os custos planejados e realizados do projeto.',
      action: 'Registrar Primeiro Custo',
    },

    columns: {
      id: 'ID',
      category: 'Categoria',
      type: 'Tipo',
      date: 'Data',
      planned: 'Planejado',
      actual: 'Realizado',
      variance: 'Variação',
      actions: 'Ações',
      total: 'Total',
    },

    rowActions: {
      duplicate: 'Duplicar',
      edit: 'Editar',
      delete: 'Excluir',
    },

    type: {
      labor: 'Mão de Obra',
      material: 'Material',
      equipment: 'Equipamento',
      service: 'Serviço',
      other: 'Outro',
    },

    form: {
      editTitle: 'Editar Custo',
      newTitle: 'Novo Lançamento de Custo',
      category: 'Categoria *',
      categoryPlaceholder: 'Ex: Desenvolvimento, Infraestrutura...',
      type: 'Tipo',
      typePlaceholder: 'Selecione o tipo',
      description: 'Descrição',
      descriptionPlaceholder: 'Detalhes do custo...',
      /** (R$) fica: a cobrança do produto é em real, não muda com o idioma. */
      plannedAmount: 'Valor Planejado (R$)',
      actualAmount: 'Valor Realizado (R$)',
      amountPlaceholder: '0.00',
      date: 'Data',
      datePlaceholder: 'Selecionar data',
    },
  },

  /** Lista de Pendências: o que trava o projeto e não é tarefa do cronograma. */
  todos: {
    title: 'Lista de Pendências',
    loading: 'Carregando pendências...',
    newTodo: 'Nova Pendência',
    filterAllStatus: 'Todos Status',
    filterAllCriticality: 'Todas Criticidades',
    copySuffix: ' (cópia)',
    item: { one: 'pendência', other: 'pendências' },
    selected: { one: '{{count}} pendência selecionada', other: '{{count}} pendências selecionadas' },
    dragHint: 'Arraste para reordenar',
    confirmDelete: 'Tem certeza que deseja excluir esta pendência?',
    firstVisit: 'Registre sua primeira pendência. O que trava o projeto e não é tarefa do cronograma fica aqui, com responsável e prazo.',

    stats: {
      total: 'total',
      open: 'abertas',
      inProgress: 'em andamento',
      critical: 'críticas',
    },

    empty: {
      title: 'Nenhuma pendência cadastrada',
      description: 'Registre as pendências do projeto para acompanhamento.',
      action: 'Registrar Primeira Pendência',
    },

    columns: {
      id: 'ID',
      demand: 'Demanda',
      area: 'Área',
      stage: 'Etapa Projeto',
      criticality: 'Criticidade',
      owner: 'Responsável',
      identifiedAt: 'Data Identificação',
      plannedAt: 'Data Planejada',
      status: 'Status',
      actions: 'Ações',
    },

    rowActions: {
      duplicate: 'Duplicar',
      edit: 'Editar',
      delete: 'Excluir',
    },

    criticality: {
      alta: 'Alta',
      media: 'Média',
      baixa: 'Baixa',
    },
    status: {
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      concluido: 'Concluído',
      cancelado: 'Cancelado',
    },

    form: {
      editTitle: 'Editar Pendência',
      newTitle: 'Nova Pendência',
      demand: 'Demanda *',
      demandPlaceholder: 'Descrição da pendência',
      area: 'Área',
      areaPlaceholder: 'Ex: TI, RH, Financeiro...',
      stage: 'Etapa do Projeto',
      stagePlaceholder: 'Ex: Planejamento, Execução...',
      criticality: 'Criticidade',
      criticalityPlaceholder: 'Selecione a criticidade',
      status: 'Status',
      statusPlaceholder: 'Selecione o status',
      owner: 'Responsável',
      ownerSelect: 'Selecione...',
      ownerPlaceholder: 'Nome do responsável',
      identifiedAt: 'Data da Identificação',
      plannedAt: 'Data Planejada',
      datePlaceholder: 'Selecionar data',
      notes: 'Observações',
      notesPlaceholder: 'Notas adicionais...',
    },
  },

  /** EAP: árvore de decomposição do escopo, painel de detalhe e exportação. */
  eap: {
    title: 'EAP',
    loading: 'Carregando EAP...',
    /** Nome do nó raiz e do arquivo exportado quando o projeto não tem nome. */
    projectFallback: 'Projeto',
    exportError: 'Erro ao exportar. Tente novamente.',
    firstVisit: 'A Estrutura Analítica do Projeto decompõe o escopo em entregas, do topo às tarefas.',

    empty: {
      title: 'Nenhuma tarefa no projeto',
      description: 'Crie tarefas no Gantt para visualizar a Estrutura Analítica do Projeto (EAP).',
    },

    /** Critério de cor dos nós. A chave é o valor do `select`. */
    colorMode: {
      default: 'Cor: Padrão',
      level: 'Cor: Por Nível',
      progress: 'Cor: Por Progresso',
      priority: 'Cor: Por Prioridade',
    },

    toolbar: {
      expandAll: 'Expandir tudo',
      collapseAll: 'Recolher tudo',
      zoomOut: 'Diminuir zoom',
      zoomIn: 'Aumentar zoom',
      fitToScreen: 'Encaixar na tela',
      shortcuts: 'Atalhos',
      publish: 'Publicar',
      /** `{{plan}}` é o nome do plano mínimo que libera a publicação. */
      publishLocked: 'Publicar — Disponível no plano {{plan}}',
      exporting: 'Exportando...',
      export: 'Exportar',
      exportPdf: 'Exportar PDF',
      exportImage: 'Exportar Imagem',
    },

    /** Painel de atalhos. `Ctrl`, `Scroll` e os sinais +/− ficam no código. */
    shortcuts: {
      title: 'Atalhos de Teclado',
      navGroup: 'Navegação',
      drag: 'Arrastar',
      dragDesc: 'Mover a tela (pan)',
      click: 'Clique',
      clickDesc: 'Selecionar nó',
      doubleClick: 'Duplo clique',
      doubleClickDesc: 'Renomear',
      zoomGroup: 'Zoom',
      wheelDesc: 'Zoom in/out',
      buttonsDesc: 'Zoom via botões',
      fit: 'Encaixar',
      fitDesc: 'Ajustar à tela',
    },

    confirmDelete: {
      title: 'Excluir tarefa',
      /** `{{name}}` é o nome da tarefa, entre aspas na frase. */
      message: 'Excluir "{{name}}"? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
    },

    /** Card de um nó da árvore. */
    node: {
      options: 'Opções',
      editDetails: 'Editar Detalhes',
      addChild: 'Adicionar Subtarefa',
      addSibling: 'Adicionar Tarefa Irmã',
      toTask: 'Converter em Tarefa',
      toGroup: 'Converter em Grupo',
      delete: 'Excluir',
      collapse: 'Recolher',
      expand: 'Expandir',
      /** Badge de prioridade. A chave casa com `task.priority` (1, 2, 3+). */
      priority: {
        low: 'Baixa',
        medium: 'Média',
        high: 'Alta',
      },
    },

    /** Painel lateral de detalhe da tarefa. */
    detail: {
      title: 'Detalhes da Tarefa',
      name: 'Nome',
      type: 'Tipo',
      select: 'Selecione',
      types: {
        task: 'Tarefa',
        group: 'Grupo',
        milestone: 'Marco',
      },
      priority: 'Prioridade',
      priorities: {
        none: 'Nenhuma',
        low: 'Baixa',
        medium: 'Média',
        high: 'Alta',
      },
      assigned: 'Responsável',
      progress: 'Progresso',
      dates: 'Datas',
      start: 'Início',
      end: 'Término',
      duration: 'Duração',
      addChild: 'Adicionar Subtarefa',
      delete: 'Excluir',
    },
  },

  /** Matriz RACI: quem executa, aprova, é consultado e é informado. */
  raci: {
    title: 'Matriz RACI',
    loading: 'Carregando matriz RACI...',
    allMembers: 'Todos os membros',
    summaryToggle: 'Resumo e validação',
    taskColumn: 'Tarefa',
    cellHint: 'Clique para atribuir',
    firstVisit: 'Clique numa célula para definir o papel: Responsável, Aprovador, Consultado ou Informado. Tarefa sem Aprovador é decisão sem dono.',

    /**
     * As letras R, A, C e I são o `raci_role` gravado no banco: não se
     * traduzem. Aqui vive só o que a legenda e o tooltip mostram, indexado por
     * elas — `short` na legenda do cabeçalho, `label` no tooltip da célula.
     */
    rolesShort: {
      R: 'Responsável',
      A: 'Aprovador',
      C: 'Consultado',
      I: 'Informado',
    },
    roles: {
      R: 'Responsável (executa)',
      A: 'Aprovador (decide)',
      C: 'Consultado (opina)',
      I: 'Informado (notificado)',
    },

    emptyTeam: {
      title: 'Nenhum membro na equipe',
      description: 'Adicione membros à equipe do projeto para começar a definir a Matriz RACI.',
      /** Frase partida porque "Equipe" (nome do botão) vai em negrito. */
      hintPre: 'Use o botão ',
      hintStrong: 'Equipe',
      hintPost: ' no cabeçalho para gerenciar membros.',
    },

    emptyTasks: {
      title: 'Nenhuma tarefa no projeto',
      description: 'Crie tarefas no Gantt para defini-las na Matriz RACI.',
    },

    summary: {
      title: 'Resumo da Matriz',
      assignments: 'Atribuições',
      complete: 'Matriz RACI completa!',
      withoutR: '{{count}} tarefa(s) sem Responsável (R)',
      withoutA: '{{count}} tarefa(s) sem Aprovador (A)',
      overloaded: '{{count}} membro(s) com sobrecarga de R',
      unassigned: '{{count}} membro(s) sem atribuição',
      /** Item da lista de sobrecarga: nome do membro e quantas tarefas tem. */
      memberTasks: '{{name}} ({{count}} tarefas)',
    },
  },

  /**
   * Central de Ajuda (wiki). Aqui está só a MOLDURA do módulo: título, busca,
   * estado vazio, aviso de plano e navegação.
   *
   * O CONTEÚDO dos artigos vive em `src/data/wiki/copy/<idioma>.ts`, carregado
   * sob demanda pelo idioma corrente.
   * São 36 artigos: traduzir artigo é projeto de conteúdo, com revisão de quem
   * escreve, e não cabe numa fatia de interface.
   */
  wiki: {
    title: 'Central de Ajuda',
    searchPlaceholder: 'Buscar artigos...',
    back: 'Voltar',
    /** Volta para o módulo de onde a ajuda contextual foi aberta. */
    backToModule: 'Voltar para {{module}}',
    emptyTitle: 'Nenhum artigo encontrado',
    emptyDescription: 'Tente uma busca diferente ou selecione outra categoria.',
    /** A data em si vem do artigo, que é conteúdo: só o prefixo é interface. */
    updatedAt: 'Atualizado em {{date}}',
    /**
     * Aviso de recurso bloqueado. A frase é cortada em duas porque o nome do
     * plano entra em NEGRITO no meio dela, e a posição dele muda com o idioma:
     * em português vem depois de "no plano", em inglês vem antes de "plan".
     */
    lockedPrefix: 'Esta funcionalidade está disponível no plano',
    lockedSuffix: '. Faça upgrade para acessar este recurso e muito mais.',
    upgrade: 'Fazer Upgrade',
    related: 'Artigos Relacionados',
    /**
     * Nome do módulo de origem, indexado pelo id usado no roteamento. São mais
     * longos que os de `shell.modules` ("Gráfico de Gantt", e não "Gantt")
     * porque a frase inteira é "Voltar para ...".
     */
    sourceModules: {
      gantt: 'Gráfico de Gantt',
      schedule: 'Cronograma',
      calendar: 'Calendário',
      todos: 'Pendências',
      raci: 'Matriz RACI',
      eap: 'EAP',
      risks: 'Gestão de Riscos',
      changes: 'Controle de Mudanças',
      costs: 'Controle de Custos',
      portfolio: 'Portfólio',
      allocation: 'Planejamento de Capacidade',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type modulesCopy = typeof modules;

export default modules;
